import * as THREE from 'three';
import { CH, LAMP_COUNT } from './lamps';

/**
 * Materials for the merged vehicle meshes (see meshes.ts for the vertex attribute layout).
 *
 * One `MeshPhysicalMaterial` per car, all sharing ONE compiled program (customProgramCacheKey), with
 * per-car uniforms for: paint colours, lamp levels (`uLamp[ch]`), loose-part matrices, the driver /
 * rack visibility, procedural wear (mud, rust, road film), burn (wrecks), and the number plate /
 * roof-sign glyphs. Roughness, metalness and clearcoat come from the `surf` attribute, so chrome,
 * rubber, paint, lenses and upholstery all live in the same draw call.
 */

/** Shared clock for the rotating blue lamp (advanced by the vehicle view). */
export const carTime = { value: 0 };
/** Shared wetness 0..1 (rain film, streaks and beads on every car; set by the vehicle view). */
export const carWet = { value: 0 };

let clearcoatOn = true;
const liveCarMaterials = new Set<THREE.MeshPhysicalMaterial>();

/** Quality hook: clearcoat sheen on medium/high, plain satin paint on low. */
export function setCarClearcoat(on: boolean): void {
  if (on === clearcoatOn) return;
  clearcoatOn = on;
  for (const m of liveCarMaterials) {
    m.clearcoat = on ? 1 : 0;
    m.needsUpdate = true;
  }
}

export interface CarUniforms {
  uPaint: { value: THREE.Color };
  uPaint2: { value: THREE.Color };
  /** Upholstery, driver's coat and skin (paint slots 3, 4, 5). */
  uTrimCol: { value: THREE.Color };
  uCoat: { value: THREE.Color };
  uSkin: { value: THREE.Color };
  /** x = show driver, y = show rack, z = roof mode (0 paint, 1 contrast paint, 2 vinyl), w = show optional secondary paint. */
  uShow: { value: THREE.Vector4 };
  uLamp: { value: Float32Array };
  uPart: { value: THREE.Matrix4[] };
  /** x = grime, y = age, z = seed, w = clearance (m). */
  uWear: { value: THREE.Vector4 };
  /** Axle z positions and arch radius for rust around the arch lips: (zFront, zRear, cy, r). */
  uArch: { value: THREE.Vector4 };
  uBurn: { value: number };
  uPlate: { value: Float32Array };
  uSign: { value: Float32Array };
  /** Blue lamp centre (local) for the rotating highlight. */
  uBeacon: { value: THREE.Vector3 };
  uRoofCol: { value: THREE.Color };
  uTime: { value: number };
  uWet: { value: number };
  /** x = metallic paint (0/1), y = flake strength, z = flat matt (0/1), w unused. */
  uFinish: { value: THREE.Vector4 };
  /**
   * Detailed (Meshy) bodies only (meshyCar.ts): x = roof line (painted texels above it, facing up, are the roof),
   * w = secondary paint mode (meshyCar.ts MESHY_SECONDARY: patrol / trade band, stripes, tipper body) and y / z
   * its limits: painted texels inside take `uPaint2`.
   */
  uMeshy: { value: THREE.Vector4 };
}

export function makeCarUniforms(): CarUniforms {
  return {
    uPaint: { value: new THREE.Color(0x888888) },
    uPaint2: { value: new THREE.Color(0xffffff) },
    uTrimCol: { value: new THREE.Color(0x2a211b) },
    uCoat: { value: new THREE.Color(0x2b2b30) },
    uSkin: { value: new THREE.Color(0xe0b394) },
    uShow: { value: new THREE.Vector4(0, 0, 0, 0) },
    uLamp: { value: new Float32Array(LAMP_COUNT) },
    uPart: { value: [new THREE.Matrix4(), new THREE.Matrix4(), new THREE.Matrix4(), new THREE.Matrix4()] },
    uWear: { value: new THREE.Vector4(0, 0, 0, 0.2) },
    uArch: { value: new THREE.Vector4(1, -1, 0.3, 0.4) },
    uBurn: { value: 0 },
    uPlate: { value: new Float32Array(8) },
    uSign: { value: new Float32Array(8) },
    uBeacon: { value: new THREE.Vector3(0, 10, 0) },
    uRoofCol: { value: new THREE.Color(0x151311) },
    uTime: carTime,
    uWet: carWet,
    uFinish: { value: new THREE.Vector4(0, 0, 0, 0) },
    uMeshy: { value: new THREE.Vector4(99, 0, 0, 0) },
  };
}

const NOISE = /* glsl */ `
float bhHash(vec3 p) {
  p = fract(p * 0.3183099 + 0.1);
  p *= 17.0;
  return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
}
float bhNoise(vec3 x) {
  vec3 i = floor(x);
  vec3 f = fract(x);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(mix(bhHash(i), bhHash(i + vec3(1, 0, 0)), f.x), mix(bhHash(i + vec3(0, 1, 0)), bhHash(i + vec3(1, 1, 0)), f.x), f.y),
             mix(mix(bhHash(i + vec3(0, 0, 1)), bhHash(i + vec3(1, 0, 1)), f.x), mix(bhHash(i + vec3(0, 1, 1)), bhHash(i + vec3(1, 1, 1)), f.x), f.y), f.z);
}
float bhGlyph(float code, vec2 uv) {
  // 8 cells across; each cell is 3 lit columns + 1 gap, 5 rows with a margin above and below.
  float cell = floor(uv.x * 8.0);
  float cx = floor(fract(uv.x * 8.0) * 4.0);
  float ry = (uv.y - 0.16) / 0.68 * 5.0;
  if (cx > 2.5 || ry < 0.0 || ry >= 5.0) return 0.0;
  int bit = int(floor(ry)) * 3 + int(cx);
  return float((int(code + 0.5) >> bit) & 1);
}
`;

const VERT_PARS = /* glsl */ `
attribute vec4 surf;
varying vec4 vSurf;
varying vec3 vLocal;
varying vec3 vObjN;
uniform mat4 uPart[4];
uniform vec4 uShow;
`;

const FRAG_PARS = /* glsl */ `
varying vec4 vSurf;
varying vec3 vLocal;
varying vec3 vObjN;
uniform vec3 uPaint;
uniform vec3 uPaint2;
uniform vec3 uTrimCol;
uniform vec3 uCoat;
uniform vec3 uSkin;
uniform vec4 uShow;
uniform float uLamp[${LAMP_COUNT}];
uniform vec4 uWear;
uniform vec4 uArch;
uniform float uBurn;
uniform float uPlate[8];
uniform float uSign[8];
uniform vec3 uBeacon;
uniform vec3 uRoofCol;
uniform float uTime;
uniform float uWet;
uniform vec4 uFinish;
uniform vec4 uMeshy;
#ifdef BH_MAP
uniform sampler2D uBodyMap;
uniform sampler2D uBodyMask;
#endif
${NOISE}
`;

const FRAG_SURFACE = /* glsl */ `
#include <color_fragment>
int bhCh = int(vSurf.w + 0.5);
float bhSlot = vSurf.x;
float bhRough = vSurf.y;
float bhMetal = vSurf.z;
float bhCoat = 0.0;
vec3 bhEmit = vec3(0.0);
// Screen-space size of a body metre: fades fine detail (flakes, flutes, ribs) out at distance.
float bhFw = fwidth(vLocal.x + vLocal.y + vLocal.z);
float bhNear = 1.0 - smoothstep(0.004, 0.014, bhFw);
// Detailed bodies: texels carry the colour; the mask says where the paint goes (R) and what is chrome (G). The
// paint keeps the model's shading (its grey shade, 0.578 linear = 1) under the car's own colour.
float bhPaintW = 1.0;
float bhShade = 1.0;
vec3 bhTexel = diffuseColor.rgb;
#ifdef BH_MAP
if (bhSlot > 5.5) {
  vec3 tc = texture2D(uBodyMap, vUv).rgb;
  vec2 mk = texture2D(uBodyMask, vUv).rg;
  bhTexel = tc;
  diffuseColor.rgb = tc;
  bhPaintW = smoothstep(0.2, 0.8, mk.r);
  // Shading only darkens (shut lines, creases): above 1 a white car blooms.
  bhShade = clamp(dot(tc, vec3(0.3333)) / 0.578, 0.25, 1.0);
  // Paint texels take the procedural paint surface (meshes.ts PAINT: rough 0.42, metal 0.18; at metal 0 a white
  // car went over the bloom threshold in sunlight), chrome is polished metal, the rest satin.
  bhRough = mix(mix(0.62, 0.42, bhPaintW), 0.16, mk.g);
  bhMetal = max(mk.g, 0.18 * bhPaintW);
  bhSlot = bhPaintW > 0.02 ? 1.0 : 0.0;
  // Secondary paint (meshyCar.ts MESHY_SECONDARY): 1 patrol band and 2 trade band between two heights, 3 racing
  // stripes on the top skin between two |x|, 4 the tipper body behind z; all but the patrol band follow uShow.w.
  if (bhPaintW > 0.5 && uMeshy.w > 0.5 && (uMeshy.w < 1.5 || uShow.w > 0.5)) {
    bool bhSec = uMeshy.w < 2.5 ? (vLocal.y > uMeshy.y && vLocal.y < uMeshy.z)
      : uMeshy.w < 3.5 ? (abs(vLocal.x) > uMeshy.y && abs(vLocal.x) < uMeshy.z && vObjN.y > 0.3)
      : vLocal.z < uMeshy.y;
    if (bhSec) bhSlot = 2.0;
  }
  if (bhPaintW > 0.5 && vLocal.y > uMeshy.x && vObjN.y > 0.55) bhCh = ${CH.ROOF};
}
#endif
if (bhSlot > 0.5 && bhSlot < 2.5) {
  diffuseColor.rgb = mix(bhTexel, (bhSlot > 1.5 ? uPaint2 : uPaint) * bhShade, bhPaintW);
  bhCoat = bhPaintW;
  if (bhSlot < 1.5 && uFinish.x > 0.5) {
    // Metallic: more metal in the base coat and a sparkle of flakes close up.
    bhMetal = 0.5;
    bhRough = 0.34;
    float fl = bhHash(floor(vLocal * 420.0 + 0.5));
    float sparkle = step(0.93, fl) * bhNear * uFinish.y;
    bhRough -= 0.22 * sparkle;
    diffuseColor.rgb *= 1.0 + 0.3 * sparkle + 0.05 * (bhNoise(vLocal * 150.0) - 0.5) * bhNear;
  }
  if (bhSlot < 1.5 && uFinish.z > 0.5) {
    // Flat matt (Pike's spray bay): rough, no metal, no clearcoat.
    bhRough = 0.86;
    bhMetal = 0.0;
    bhCoat = 0.0;
    diffuseColor.rgb *= 0.92;
  }
} else if (bhSlot > 2.5) {
  diffuseColor.rgb = bhSlot < 3.5 ? uTrimCol : bhSlot < 4.5 ? uCoat : uSkin;
}
bool bhPlate = vUv.y > 1.5 && vUv.y < 3.5;
bool bhSignFace = vUv.y > 3.5 && vUv.y < 5.5;
bool bhLens = vUv.y > 5.5;
if (bhCh == ${CH.ROOF} && uShow.z > 0.5) {
  diffuseColor.rgb = uRoofCol;
  if (uShow.z > 1.5) {
    // Vinyl: matte, fine grain, no clearcoat.
    float grain = bhNoise(vLocal * 60.0);
    diffuseColor.rgb *= 0.85 + 0.3 * grain;
    bhRough = 0.78;
    bhMetal = 0.0;
    bhCoat = 0.0;
  }
}
if (bhPlate) {
  // Pre-1973 British plate: silver-white characters on black.
  float code = uPlate[int(clamp(floor(vUv.x * 8.0), 0.0, 7.0))];
  float lit = bhGlyph(code, vec2(vUv.x, vUv.y - 2.0));
  diffuseColor.rgb = mix(vec3(0.02), vec3(0.78, 0.78, 0.74), lit);
  bhRough = mix(0.7, 0.3, lit);
  bhMetal = mix(0.0, 0.6, lit);
}
bool bhLamp = bhCh >= 1 && bhCh < ${LAMP_COUNT};
if (bhLamp) {
  float lv = uLamp[bhCh];
  if (lv < 0.0) {
    // Smashed lens: dark, rough, a few glints of what is left.
    diffuseColor.rgb = diffuseColor.rgb * 0.12 + vec3(0.02) * step(0.7, bhNoise(vLocal * 90.0));
    bhRough = 0.85;
  } else {
    float k = lv;
    float core = 1.0;
    if (bhLens) {
      // Headlamp: fluted glass over a silvered reflector, a bright bulb at the centre when lit.
      vec2 c = vec2(vUv.x, vUv.y - 6.0) * 2.0 - 1.0;
      float r = length(c);
      float flute = 0.5 + 0.5 * sin(r * 24.0);
      flute = mix(0.5, flute, bhNear);
      float bulb = exp(-r * r * 4.0);
      diffuseColor.rgb = mix(vec3(0.56, 0.57, 0.55), vec3(0.9, 0.9, 0.88), 0.35 * flute + 0.4 * bulb);
      bhRough = 0.16 + 0.22 * flute;
      bhMetal = 0.75;
      core = 0.45 + 1.7 * bulb + 0.35 * flute;
    } else if (bhCh == ${CH.TAIL} || bhCh == ${CH.REVERSE} || (bhCh >= ${CH.IND_FL} && bhCh <= ${CH.IND_RR})) {
      // Ribbed plastic lens.
      float rib = 0.5 + 0.5 * sin(vLocal.y * 240.0 + vLocal.x * 20.0);
      rib = mix(0.5, rib, bhNear);
      diffuseColor.rgb *= 0.85 + 0.3 * rib;
      bhRough = 0.1 + 0.18 * rib;
      core = 0.85 + 0.3 * rib;
    }
    if (bhCh == ${CH.BEACON}) {
      // Rotating reflector: a bright lobe sweeping round the dome.
      float a = atan(vLocal.x - uBeacon.x, vLocal.z - uBeacon.z + 1e-4);
      float sweep = pow(max(0.0, cos(a - uTime * 9.0)), 6.0);
      k = lv * (0.12 + 1.6 * sweep);
    }
    if (bhSignFace) {
      float code = uSign[int(clamp(floor(vUv.x * 8.0), 0.0, 7.0))];
      float lit = bhGlyph(code, vec2(vUv.x, vUv.y - 4.0));
      diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.05, 0.05, 0.06), lit);
      k *= 1.0 - 0.9 * lit;
    }
    bhEmit = diffuseColor.rgb * k * core;
    if (k > 0.05) diffuseColor.rgb = mix(diffuseColor.rgb, vec3(1.0), 0.15);
  }
}
// Seeded wear on the lower body: mud splash, road film on the tail, rust near sills and arch lips.
// Rubber, seams and liners (roughness >= 0.9) are already matt black: mud on them only lightened
// them into sandy slabs (running boards, sills) that read as floating parts.
if (!bhPlate && bhCh != ${CH.DRIVER} && bhSlot < 2.5 && !bhLamp && bhRough < 0.9) {
  float grime = uWear.x;
  float age = uWear.y;
  float seed = uWear.z;
  float y = vLocal.y - uWear.w;
  float side = abs(vObjN.x);
  float n1 = bhNoise(vec3(vLocal.z * 3.1 + seed, vLocal.y * 9.0, vLocal.x * 2.0));
  float mudLine = 0.04 + grime * 0.22 * (0.55 + 0.6 * n1);
  float mud = (1.0 - smoothstep(mudLine - 0.04, mudLine + 0.05, y)) * (0.15 + 0.55 * grime);
  float tail = step(0.5, -vObjN.z) * (1.0 - smoothstep(0.1, 0.32 + 0.25 * grime, y)) * grime * 0.7;
  mud = max(mud, tail) * (0.6 + 0.5 * bhNoise(vLocal * 14.0 + seed));
  // A soft film of road dirt creeping up the flanks, heavier on dirtier cars.
  float film = (1.0 - smoothstep(0.1, 0.55, y)) * grime * 0.22 * (0.7 + 0.6 * bhNoise(vLocal * 5.0 + seed));
  mud = max(mud, film);
  vec3 mudCol = vec3(0.16, 0.13, 0.09) * (0.8 + 0.4 * n1);
  diffuseColor.rgb = mix(diffuseColor.rgb, mudCol, clamp(mud, 0.0, 0.6));
  bhRough = mix(bhRough, 0.95, clamp(mud * 1.3, 0.0, 1.0));
  bhMetal *= 1.0 - clamp(mud, 0.0, 1.0);
  bhCoat *= 1.0 - clamp(mud * 1.5, 0.0, 1.0);
  if (bhSlot > 0.5 && age > 0.45) {
    float dF = length(vec2(vLocal.z - uArch.x, vLocal.y - uArch.z)) - uArch.w;
    float dR = length(vec2(vLocal.z - uArch.y, vLocal.y - uArch.z)) - uArch.w;
    float nearLip = 1.0 - smoothstep(0.0, 0.12, min(abs(dF), abs(dR)));
    float nearSill = 1.0 - smoothstep(0.05, 0.3, y);
    float spots = smoothstep(0.68, 0.86, bhNoise(vLocal * 7.0 + seed * 3.0));
    float rust = spots * max(nearLip, nearSill) * side * clamp((age - 0.45) * 3.0, 0.0, 1.0);
    vec3 rustCol = mix(vec3(0.22, 0.1, 0.045), vec3(0.36, 0.19, 0.08), bhNoise(vLocal * 30.0));
    diffuseColor.rgb = mix(diffuseColor.rgb, rustCol, rust);
    bhRough = mix(bhRough, 0.9, rust);
    bhCoat *= 1.0 - rust;
    // Older paint has gone a little chalky.
    diffuseColor.rgb = mix(diffuseColor.rgb, vec3(dot(diffuseColor.rgb, vec3(0.33))), (age - 0.45) * 0.35);
  }
  // Rain: a water film darkens and polishes everything, with streaks running down the flanks and
  // beads standing on the horizontal panels.
  if (uWet > 0.01) {
    float up = clamp(vObjN.y, 0.0, 1.0);
    float streak = smoothstep(0.55, 0.85, bhNoise(vec3(vLocal.x * 26.0, vLocal.y * 1.4 + seed, vLocal.z * 26.0))) * (1.0 - up);
    float beads = step(0.88, bhNoise(vLocal * 90.0 + seed)) * up * bhNear;
    float water = uWet * clamp(0.55 + 0.5 * streak + 0.8 * beads, 0.0, 1.0);
    bhRough = mix(bhRough, 0.05, water * 0.9);
    diffuseColor.rgb *= 1.0 - 0.22 * uWet * (0.6 + 0.4 * streak);
    // The film mirrors the sky a little along the streaks and on the beads.
    diffuseColor.rgb += vec3(0.03, 0.035, 0.045) * uWet * (0.6 * streak + beads);
    bhCoat = max(bhCoat, uWet * 0.8 * step(0.5, bhSlot));
  }
}
if (uBurn > 0.0) {
  // Burnt out: blistered black and brown metal, ash-grey where the paint has gone.
  float n = bhNoise(vLocal * 5.0);
  float n2 = bhNoise(vLocal * 23.0 + 7.0);
  vec3 charCol = mix(vec3(0.025, 0.022, 0.02), vec3(0.11, 0.09, 0.075), n);
  charCol = mix(charCol, vec3(0.26, 0.12, 0.05), smoothstep(0.6, 0.85, n2) * 0.8);
  charCol = mix(charCol, vec3(0.3, 0.29, 0.27), smoothstep(0.8, 0.97, n) * step(0.0, vObjN.y) * 0.35);
  diffuseColor.rgb = mix(diffuseColor.rgb, charCol, uBurn);
  bhRough = mix(bhRough, 0.96, uBurn);
  bhMetal = mix(bhMetal, 0.15, uBurn);
  bhCoat *= 1.0 - uBurn;
  bhEmit *= 1.0 - uBurn;
}
`;

/**
 * Crumpled panels can fold triangles flat; under 4x MSAA some rasterisers then shade samples with
 * garbage normals, and one NaN pixel blooms into a black frame. Clamp it to black at the source.
 */
const NAN_GUARD = `#include <dithering_fragment>
if (any(isnan(gl_FragColor)) || any(isinf(gl_FragColor))) gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);`;

function patchCar(shader: THREE.WebGLProgramParametersWithUniforms, u: CarUniforms): void {
  Object.assign(shader.uniforms, u);
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', `#include <common>\n${VERT_PARS}`)
    .replace(
      '#include <beginnormal_vertex>',
      `#include <beginnormal_vertex>
int bhCh = int(surf.w + 0.5);
if (bhCh >= ${CH.BONNET} && bhCh <= ${CH.BOOT}) objectNormal = mat3(uPart[bhCh - ${CH.BONNET}]) * objectNormal;
vObjN = objectNormal;`,
    )
    .replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
vSurf = surf;
vLocal = position;
if (bhCh >= ${CH.BONNET} && bhCh <= ${CH.BOOT}) transformed = (uPart[bhCh - ${CH.BONNET}] * vec4(transformed, 1.0)).xyz;
if ((bhCh == ${CH.DRIVER} && uShow.x < 0.5) || (bhCh == ${CH.RACK} && uShow.y < 0.5) || (surf.x > 1.5 && surf.x < 2.5 && uShow.w < 0.5)) transformed = vec3(0.0);`,
    );
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', `#include <common>\n${FRAG_PARS}`)
    .replace('#include <color_fragment>', FRAG_SURFACE)
    .replace('#include <roughnessmap_fragment>', 'float roughnessFactor = bhRough;')
    .replace('#include <dithering_fragment>', NAN_GUARD)
    .replace('#include <metalnessmap_fragment>', 'float metalnessFactor = bhMetal;')
    .replace('#include <emissivemap_fragment>', 'totalEmissiveRadiance += bhEmit;')
    .replace(
      '#include <lights_physical_fragment>',
      `#include <lights_physical_fragment>
#ifdef USE_CLEARCOAT
material.clearcoat *= bhCoat;
#endif`,
    );
}

/** A car-body material (one per car; every car shares the compiled program). */
export function createCarMaterial(u: CarUniforms, maps: { color: THREE.Texture; mask: THREE.Texture } | null = null): THREE.MeshPhysicalMaterial {
  const m = new THREE.MeshPhysicalMaterial({
    vertexColors: true,
    roughness: 0.5,
    metalness: 0,
    clearcoat: clearcoatOn ? 1 : 0,
    clearcoatRoughness: 0.12,
    envMapIntensity: 1.1,
  });
  // `vUv` is only declared when a map is used; the plate/sign glyphs need it, so force USE_UV.
  m.defines = maps ? { USE_UV: '', BH_MAP: '' } : { USE_UV: '' };
  // Detailed bodies (meshyCar.ts): the same shader plus the texture path, one more program for every such car.
  const extra = maps ? { uBodyMap: { value: maps.color }, uBodyMask: { value: maps.mask } } : {};
  m.onBeforeCompile = (shader) => {
    patchCar(shader, u);
    Object.assign(shader.uniforms, extra);
  };
  m.customProgramCacheKey = () => (maps ? 'bh-car-2-map' : 'bh-car-2');
  liveCarMaterials.add(m);
  return m;
}

export function releaseCarMaterial(m: THREE.Material): void {
  liveCarMaterials.delete(m as THREE.MeshPhysicalMaterial);
  m.dispose();
}

// ------------------------------------------------------------------------------------------ glass

export interface GlassUniforms {
  /** Per pane (front, rear, left, right): 0 intact, 1 cracked, 2 gone. */
  uPane: { value: THREE.Vector4 };
  uBurn: { value: number };
  uWet: { value: number };
}

export function makeGlassUniforms(): GlassUniforms {
  return { uPane: { value: new THREE.Vector4() }, uBurn: { value: 0 }, uWet: carWet };
}

/**
 * Tinted, slightly see-through glass so the seats, wheel and driver read through it. Cracked panes
 * get a crazed, frosted pattern; panes that are gone are discarded.
 */
export function createGlassMaterial(u: GlassUniforms): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({
    // Dark smoky glass with a restrained sky reflection. Blending happens in linear HDR before tone
    // mapping, so 26% transmission still looks ~55% see-through on screen: at 0.62-0.74 opacity the
    // panes vanished and the roof read as a slab floating on pillars. ~0.88 keeps the greenhouse
    // darker than the paint from the high camera while seats, wheel and driver still show through.
    color: 0x0b1117,
    roughness: 0.1,
    metalness: 0.2,
    transparent: true,
    opacity: 0.88,
    envMapIntensity: 1.0,
  });
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, u);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec4 surf;\nvarying float vPane;\nvarying vec3 vLocal;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvPane = surf.w;\nvLocal = position;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\nvarying float vPane;\nvarying vec3 vLocal;\nuniform vec4 uPane;\nuniform float uBurn;\nuniform float uWet;\n${NOISE}`)
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
int bhP = int(vPane + 0.5);
float bhState = bhP == 0 ? uPane.x : bhP == 1 ? uPane.y : bhP == 2 ? uPane.z : uPane.w;
if (bhState > 1.5) discard;
float bhCrack = 0.0;
if (bhState > 0.5) {
  // Crazed safety glass: a web of bright lines, denser near a random impact point.
  // Lines are filtered by their screen-space width so they fade into an even milky frosting with
  // distance instead of aliasing into sparkle from the gameplay camera.
  vec3 q = vLocal * 4.5;
  float web = abs(bhNoise(q) - 0.5) + abs(bhNoise(q * 2.3 + 4.0) - 0.5) * 0.6;
  float fw = fwidth(web);
  float lines = 1.0 - smoothstep(0.012, 0.03 + fw * 1.5, web);
  float far = smoothstep(0.012, 0.045, fw);
  bhCrack = mix(lines * 0.6, 0.18, far);
  diffuseColor.rgb = mix(diffuseColor.rgb * 1.5 + 0.04, vec3(0.46, 0.49, 0.5), bhCrack);
  diffuseColor.a = mix(0.9, 0.97, bhCrack);
}
// Rain on the glass: beads of water that catch the light.
float bhDrops = 0.0;
if (uWet > 0.01) {
  float fw = fwidth(vLocal.x + vLocal.y + vLocal.z);
  float near = 1.0 - smoothstep(0.004, 0.014, fw);
  bhDrops = step(0.86, bhNoise(vec3(vLocal.x * 70.0, vLocal.y * 160.0, vLocal.z * 70.0))) * uWet * near;
  diffuseColor.rgb += vec3(0.06) * bhDrops;
}
diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.02), uBurn);`,
      )
      .replace('#include <roughnessmap_fragment>', 'float roughnessFactor = mix(mix(roughness, 0.6, bhCrack), 0.02, bhDrops);')
      .replace('#include <dithering_fragment>', NAN_GUARD);
  };
  m.customProgramCacheKey = () => 'bh-glass-2';
  return m;
}

// ------------------------------------------------------------------------------------------ wheels

/** Shared instanced-wheel material: roughness/metalness from `surf`, burnt tint via instanceColor. */
export function createWheelMaterial(): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.8, metalness: 0, envMapIntensity: 1.1 });
  m.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec4 surf;\nvarying vec4 vSurf;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvSurf = surf;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec4 vSurf;')
      .replace('#include <roughnessmap_fragment>', 'float roughnessFactor = vSurf.y;')
      .replace('#include <metalnessmap_fragment>', 'float metalnessFactor = vSurf.z;');
    // Rim colour (Pike's spray bay): per-instance `rimTint` (rgb, w = painted) over the parts tagged slot 1.
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\n#ifdef USE_INSTANCING\nattribute vec4 rimTint;\n#endif\nvarying vec4 vRim;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n#ifdef USE_INSTANCING\nvRim = rimTint;\n#else\nvRim = vec4(0.0);\n#endif');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec4 vRim;')
      .replace('#include <color_fragment>', '#include <color_fragment>\nif (vSurf.x > 0.5 && vRim.w > 0.5) diffuseColor.rgb = vRim.rgb;');
  };
  m.customProgramCacheKey = () => 'bh-wheel-2';
  return m;
}

// ------------------------------------------------------------------------------------------ light pools

export interface LightUniforms {
  /** x = head pools, y = head beams (volumetric, HIGH only), z = tail/brake glow, w = reverse. */
  uLevel: { value: THREE.Vector4 };
}

export function makeLightUniforms(): LightUniforms {
  return { uLevel: { value: new THREE.Vector4() } };
}

/**
 * Additive light on the road and in the air in front of a car: per-vertex colour carries the
 * falloff, `lch` picks which level scales it. One draw call per lit car.
 */
export function createLightMaterial(u: LightUniforms): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: u as unknown as Record<string, THREE.IUniform>,
    vertexShader: /* glsl */ `
      attribute float lch;
      attribute vec3 color;
      uniform vec4 uLevel;
      varying vec3 vCol;
      void main() {
        float k = lch < 0.5 ? uLevel.x : lch < 1.5 ? uLevel.y : lch < 2.5 ? uLevel.z : uLevel.w;
        vCol = color * k;
        vec3 p = k > 0.001 ? position : vec3(0.0);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      varying vec3 vCol;
      void main() { gl_FragColor = vec4(vCol, 1.0); }`,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    // Additive, so face order can't change the sum: one pass, not three's back-then-front pair, which made
    // this two draws (and two program re-fetches) per lit car (docs/PERF.md "Phone pass").
    forceSinglePass: true,
  });
}
